import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { MdCheckCircle, MdRefresh } from 'react-icons/md';
import Card from 'components/card';
import { CONFIG } from 'variables/config';
import { CatalogIndustry, suggestProduct } from 'views/backoffice/leads/BatiwebForwardCell';
import ProductPicker from './ProductPicker';

// « Produits orphelins » : couples industrie / produit recopiés sur des offres, bons de commande,
// templates, leads… mais absents du catalogue (renommés ou supprimés). Chaque ligne se réattribue à
// un produit existant, partout à la fois (GET / POST /industry/mismatches, services/subIndustryCatalog.ts).

type Source = 'offers' | 'sellerOffers' | 'purchaseOrders' | 'templates' | 'landingPageVisits' | 'leads' | 'diagnostics' | 'sellerPrices';

const SOURCE_LABELS: Record<Source, string> = {
    offers: 'Offres marketplace',
    sellerOffers: 'Offres directes',
    purchaseOrders: 'Bons de commande',
    templates: 'Templates',
    landingPageVisits: 'Pages (Pixel)',
    leads: 'Leads',
    diagnostics: 'Diagnostics',
    sellerPrices: 'Prix vendeurs',
};
const SOURCE_ORDER = Object.keys(SOURCE_LABELS) as Source[];

interface Mismatch {
    industry: string;
    subIndustry: string;
    industryUnknown: boolean;
    counts: Partial<Record<Source, number>>;
    total: number;
}

const rowKey = (m: Mismatch) => `${m.industry}|${m.subIndustry}`;

export default function BackOfficeProductMismatches() {
    const [mismatches, setMismatches] = useState<Mismatch[]>([]);
    const [industries, setIndustries] = useState<CatalogIndustry[]>([]);
    const [choices, setChoices] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const [mismatchRes, industryRes] = await Promise.all([
                axios.get(`${CONFIG.apiUrl}/industry/mismatches`, { headers }),
                axios.get(`${CONFIG.apiUrl}/industry`, { headers }),
            ]);
            const list: Mismatch[] = mismatchRes.data.mismatches;
            setMismatches(list);
            setIndustries(industryRes.data);
            // Pré-sélection : le produit du catalogue au nom le plus proche (sinon rien).
            setChoices(Object.fromEntries(list.map((m) => [rowKey(m), suggestProduct(industryRes.data, m.industry, m.subIndustry)])));
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Échec du chargement des produits orphelins');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reassign = async (m: Mismatch) => {
        const key = rowKey(m);
        const choice = choices[key];
        if (!choice) return;
        const [industry, subIndustry] = choice.split('|');
        const from = `${m.industry} / ${m.subIndustry || '(sans produit)'}`;
        if (!window.confirm(`Remplacer « ${from} » par « ${industry} / ${subIndustry} » partout (${m.total} élément(s), leads compris) ?`)) return;
        setBusyKey(key);
        setError(null);
        setNotice(null);
        try {
            const res = await axios.post(
                `${CONFIG.apiUrl}/industry/mismatches/reassign`,
                { from: { industry: m.industry, subIndustry: m.subIndustry }, to: { industry, subIndustry } },
                { headers }
            );
            const conflicts: number = res.data.sellerPriceConflicts || 0;
            setMismatches((prev) => (conflicts ? prev : prev.filter((x) => rowKey(x) !== key)));
            setNotice(
                conflicts
                    ? `« ${from} » réattribué. ${conflicts} prix vendeur(s) non déplacé(s) : le vendeur a déjà un prix sur « ${subIndustry} ».`
                    : `« ${from} » réattribué à « ${industry} / ${subIndustry} ».`
            );
            if (conflicts) load();
        } catch (e: any) {
            setError(e?.response?.data?.message || 'Échec de la réattribution');
        } finally {
            setBusyKey(null);
        }
    };

    return (
        <div className="mt-3 flex flex-col gap-5">
            <Card extra="p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">Produits orphelins</h2>
                        <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
                            Produits (industrie / sous-industrie) encore présents sur des offres, bons de commande, templates, leads…
                            mais absents du catalogue, en général parce qu&apos;ils ont été renommés ou supprimés. Leurs leads ne
                            matchent plus et ne partent pas chez Batiweb. Choisissez le bon produit : il remplace l&apos;ancien
                            partout à la fois. Le catalogue n&apos;est pas modifié.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
                    >
                        <MdRefresh /> Actualiser
                    </button>
                </div>
                {notice && <p className="mt-3 text-sm text-green-600 dark:text-green-400">{notice}</p>}
                {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
            </Card>

            <Card extra="p-6">
                {loading ? (
                    <p className="py-10 text-center text-gray-500">Chargement…</p>
                ) : mismatches.length === 0 ? (
                    <p className="flex items-center justify-center gap-2 py-10 text-center text-gray-500">
                        <MdCheckCircle className="text-green-500" /> Aucun produit orphelin : tout correspond au catalogue.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 dark:border-navy-600">
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Produit inconnu</th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Utilisé par</th>
                                    <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Réattribuer à</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mismatches.map((m) => {
                                    const key = rowKey(m);
                                    return (
                                        <tr key={key} className="border-b border-gray-100 align-top last:border-0 dark:border-navy-700">
                                            <td className="py-3 pr-4">
                                                <div className="font-medium text-navy-700 dark:text-white">
                                                    {m.subIndustry || <span className="italic text-gray-400">(sans produit)</span>}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    {m.industry}
                                                    {m.industryUnknown && <span className="ml-1 font-medium text-amber-600 dark:text-amber-400">— industrie inconnue</span>}
                                                </div>
                                            </td>
                                            <td className="py-3 pr-4">
                                                <div className="flex max-w-md flex-wrap gap-1">
                                                    {SOURCE_ORDER.filter((s) => m.counts[s]).map((s) => (
                                                        <span key={s} className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-navy-700 dark:text-gray-300">
                                                            {SOURCE_LABELS[s]} : {m.counts[s]}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="py-3">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <ProductPicker
                                                        industries={industries}
                                                        value={choices[key] || ''}
                                                        onChange={(next) => setChoices((prev) => ({ ...prev, [key]: next }))}
                                                        disabled={busyKey === key}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => reassign(m)}
                                                        disabled={!choices[key] || !!busyKey}
                                                        className="rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {busyKey === key ? 'En cours…' : 'Réattribuer'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}
